import { useEffect, useMemo, useRef, useState } from 'react';

import Modal from '@/components/Modal';
import ThemedIconButton from '@/components/ThemedIconButton';
import { setShareModalOpen, setStartShare } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import * as styles from './styles.module.scss';

import { sendEventV2 } from '@/utils/analytics';
import { isDiscordOnMobile } from '@/components/DiscordAuthFlow/useDiscord';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import { useUITranslatedText } from '@/components/TranslatedText';

export default function ShareModal() {
    const dispatch = useAppDispatch();
    const { shareModalOpen, shareMessage, shareURL, shareModalTitle } = useAppSelector((state) => state.app);
    const i16n_shareModalTitle = useUITranslatedText('shareModalTitle', shareModalTitle);
    const i16n_share = useUITranslatedText('share', 'Share');
    const i16n_copy = useUITranslatedText('copy', 'Copy');
    const i16n_copied = useUITranslatedText('copied', 'Copied!');

    const [copyText, setCopyText] = useState(i16n_copy);
    const copyTextTimeout = useRef<number>(0);

    const url = encodeURIComponent(shareURL);
    const title = encodeURIComponent(shareMessage);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&title=${title}`;
    const twitterUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;

    function open(url: string) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    // Track share button click
    function trackShare(target: string) {
        sendEventV2('share', [target, shareMessage, shareURL]);
    }

    useEffect(() => {
        if (shareModalOpen) {
            sendEventV2('arise_share_modal_open');
        }
    }, [shareModalOpen]);

    const isDiscordMobile = useMemo(() => {
        try {
            return IS_DISCORD_ACTIVITY && isDiscordOnMobile();
        } catch (error) {
            return false;
        }
    }, []);

    return (
        <Modal
            title={i16n_shareModalTitle}
            onClose={() => {
                dispatch(setShareModalOpen(false));
            }}
            isVisible={shareModalOpen}
            zIndex={130}
        >
            <div className={styles.buttonContainer}>
                {!isDiscordMobile && (
                    <>
                        <ThemedIconButton
                            text={i16n_share}
                            onClick={() => {
                                trackShare('facebook');
                                open(facebookUrl);
                            }}
                            disabled={false}
                            icon="facebook"
                        />
                        <ThemedIconButton
                            text={i16n_share}
                            onClick={() => {
                                trackShare('twitter');
                                open(twitterUrl);
                            }}
                            disabled={false}
                            icon="twitter"
                        />
                    </>
                )}
                <ThemedIconButton
                    text={copyText}
                    onClick={() => {
                        try {
                            const copyMessage = shareMessage + ' \n' + shareURL;
                            navigator.clipboard.writeText(copyMessage);
                            setCopyText(i16n_copied);
                            clearTimeout(copyTextTimeout.current);
                            copyTextTimeout.current = setTimeout(() => {
                                setCopyText(i16n_copy);
                            }, 2000) as any as number;

                            trackShare('copy');
                        } catch (error) {
                            console.error('Failed to copy: ', error);
                        }
                    }}
                    disabled={false}
                    icon="copy"
                />
            </div>
        </Modal>
    );
}
